// War-zones overlay — hardcoded GeoJSON polygons for currently-active
// armed conflict regions, rendered as red translucent fills with a
// pulsing dashed boundary. Works independently of the live signal feed
// (always-on situational backdrop) so the map shows context even before
// the first ACLED event arrives.
//
// Design intent (matches `doc/intel-page-design-plan.md`):
//   * Communicates "where in the world things are bad" at a glance, even
//     at world zoom where individual conflict events are too small to see.
//   * Sits BELOW the icon/heatmap layers in the composition order so
//     individual events still pop on top.
//   * Hand-curated, not auto-derived from signals — these are official /
//     consensus armed conflict regions per ACLED + UCDP + UN OCHA. Update
//     monthly as the geopolitical map shifts.
//
// **Data provenance**: each polygon is a simplified bounding region (NOT
// front lines or operational maps — those are CUI/RESTRICTED). These are
// public, OPEN-classification regional approximations sufficient for
// "this country is in active armed conflict" cueing.
//
// To update: edit the WAR_ZONES constant. Polygons are GeoJSON
// `[lng,lat]` arrays. Keep ~5-10 vertices per polygon (the renderer
// upsamples; more vertices just add network bytes for no visual gain).

import { PolygonLayer } from '@deck.gl/layers';
import type { intel as i } from '@eidollon/contracts';
import { type MapPalette, type RGBA, getMapPalette } from '../colors.js';
// WarZoneInfo is a type-only import — no runtime cycle even though registry.ts
// imports the warZonesLayer value from this file (TS erases type imports).
import type { DeckLayerProps, LayerSpec, WarZoneInfo } from './registry.js';

interface WarZone {
  id: string;
  name: string;
  /** ISO-8601 month the conflict was last updated in this layer. */
  asOf: string;
  /** Closed polygon, [lng,lat] vertices, last vertex == first. */
  polygon: number[][];
  /** Severity 0-100 driving fill alpha + boundary intensity. */
  severity: number;
  /** Short tag shown on hover. */
  tag: string;
}

// Curated list of currently-active armed conflicts (≥1k fatalities/yr).
// Sources: UCDP/PRIO Armed Conflict Dataset 2025, ACLED Conflict Index,
// ICG CrisisWatch. Polygons are simplified bounding regions.
const WAR_ZONES: readonly WarZone[] = [
  {
    id: 'ukraine',
    name: 'Ukraine — Russian invasion',
    asOf: '2026-04',
    severity: 95,
    tag: 'state-vs-state · NATO interest',
    polygon: [
      [22.1, 52.4],
      [40.2, 52.4],
      [40.2, 44.4],
      [32.4, 44.4],
      [29.8, 45.2],
      [22.1, 48.5],
      [22.1, 52.4],
    ],
  },
  {
    id: 'gaza',
    name: 'Gaza · Israel-Hamas',
    asOf: '2026-04',
    severity: 92,
    tag: 'urban siege · humanitarian crisis',
    polygon: [
      [34.21, 31.6],
      [34.59, 31.6],
      [34.59, 31.21],
      [34.21, 31.21],
      [34.21, 31.6],
    ],
  },
  {
    id: 'lebanon',
    name: 'South Lebanon · Israel-Hezbollah',
    asOf: '2026-04',
    severity: 78,
    tag: 'cross-border strikes',
    polygon: [
      [35.1, 33.9],
      [35.85, 33.9],
      [35.85, 33.05],
      [35.1, 33.05],
      [35.1, 33.9],
    ],
  },
  {
    id: 'sudan',
    name: 'Sudan · SAF-RSF civil war',
    asOf: '2026-04',
    severity: 90,
    tag: 'civil war · famine risk',
    polygon: [
      [22.0, 22.0],
      [38.6, 22.0],
      [38.6, 8.7],
      [27.9, 8.7],
      [22.0, 12.0],
      [22.0, 22.0],
    ],
  },
  {
    id: 'myanmar',
    name: 'Myanmar · junta vs PDF + EAOs',
    asOf: '2026-04',
    severity: 85,
    tag: 'civil war · ethnic conflict',
    polygon: [
      [92.2, 28.5],
      [101.2, 28.5],
      [101.2, 9.6],
      [97.6, 9.6],
      [92.2, 20.5],
      [92.2, 28.5],
    ],
  },
  {
    id: 'yemen',
    name: 'Yemen · Houthi / Saudi coalition',
    asOf: '2026-04',
    severity: 80,
    tag: 'civil war · Red Sea shipping risk',
    polygon: [
      [42.6, 19.0],
      [53.1, 19.0],
      [53.1, 12.1],
      [42.6, 12.1],
      [42.6, 19.0],
    ],
  },
  {
    id: 'drc-east',
    name: 'Eastern DRC · M23 + ADF',
    asOf: '2026-04',
    severity: 82,
    tag: 'rebel insurgency',
    polygon: [
      [27.8, 0.5],
      [31.3, 0.5],
      [31.3, -3.5],
      [27.8, -3.5],
      [27.8, 0.5],
    ],
  },
  {
    id: 'sahel',
    name: 'Sahel · jihadist insurgency (Mali/BF/Niger)',
    asOf: '2026-04',
    severity: 80,
    tag: 'multi-state insurgency',
    polygon: [
      [-12.0, 17.5],
      [16.0, 17.5],
      [16.0, 11.0],
      [-12.0, 11.0],
      [-12.0, 17.5],
    ],
  },
  {
    id: 'haiti',
    name: 'Haiti · gang collapse',
    asOf: '2026-04',
    severity: 75,
    tag: 'state collapse · gangs',
    polygon: [
      [-74.5, 20.1],
      [-71.6, 20.1],
      [-71.6, 17.9],
      [-74.5, 17.9],
      [-74.5, 20.1],
    ],
  },
  {
    id: 'somalia',
    name: 'Somalia · al-Shabaab',
    asOf: '2026-04',
    severity: 72,
    tag: 'jihadist insurgency',
    polygon: [
      [40.9, 11.9],
      [51.4, 11.9],
      [51.4, -1.7],
      [40.9, -1.7],
      [40.9, 11.9],
    ],
  },
  {
    id: 'cabo-delgado',
    name: 'Cabo Delgado · ISIS-Mozambique',
    asOf: '2026-04',
    severity: 68,
    tag: 'jihadist insurgency · LNG infrastructure risk',
    polygon: [
      [38.5, -10.5],
      [40.6, -10.5],
      [40.6, -13.8],
      [38.5, -13.8],
      [38.5, -10.5],
    ],
  },
];

// Severity → RGBA color stops, sourced from the palette's warZoneFill /
// warZoneLine groups so the four-stop ramp stays in lockstep with the legend.
//   ≥90 → extreme  (red, alpha 90 / 230)
//   75-89 → high   (orange-red, alpha 70 / 200)
//   60-74 → elevated (amber, alpha 55 / 180)
//   <60 → low      (yellow, alpha 40 / 160)
//
// We take the palette as an argument so deck.gl callbacks read from a
// snapshot captured at builder-call-time (one snapshot per re-render),
// rather than `getMapPalette()` once per row.
function severityToFillColor(palette: MapPalette, sev: number): RGBA {
  if (sev >= 90) return palette.warZoneFill.extreme;
  if (sev >= 75) return palette.warZoneFill.high;
  if (sev >= 60) return palette.warZoneFill.elevated;
  return palette.warZoneFill.low;
}
function severityToLineColor(palette: MapPalette, sev: number): RGBA {
  if (sev >= 90) return palette.warZoneLine.extreme;
  if (sev >= 75) return palette.warZoneLine.high;
  if (sev >= 60) return palette.warZoneLine.elevated;
  return palette.warZoneLine.low;
}

// Layer is always-visible (independent of feed); we conform to the
// LayerSpec interface but `signalTypes` is empty + `select` returns []
// because this isn't a signal layer.
export const warZonesLayer: LayerSpec = {
  id: 'war-zones',
  label: 'WZ · armed conflict regions',
  // 'heatmap' is the closest existing kind; the registry uses it to
  // prevent collapse-when-too-many-markers. War-zones aren't markers;
  // they're persistent context polygons.
  kind: 'heatmap',
  signalTypes: [] as readonly i.SignalType[],
  color: '#ff4757',
  colorAxis: 'category',
  description: 'Curated active armed-conflict regions (UCDP + ACLED + ICG)',
  classification: 'OPEN',
  legend: {
    swatch: '#ff4757',
    label: 'WAR ZONES',
    hint: 'curated active armed-conflict regions (UCDP/PRIO + ACLED + ICG)',
  },
  select: () => [],
  visibleAtZoom: (z) => z <= 12, // hide when zoomed past city scale
  deckLayer: ({ onWarZoneClick }: DeckLayerProps) => {
    const palette = getMapPalette();
    return new PolygonLayer<WarZone>({
      id: 'intel-war-zones',
      data: WAR_ZONES as WarZone[],
      pickable: true,
      stroked: true,
      filled: true,
      getPolygon: (z) => z.polygon,
      getFillColor: (z) => severityToFillColor(palette, z.severity),
      getLineColor: (z) => severityToLineColor(palette, z.severity),
      lineWidthUnits: 'pixels',
      getLineWidth: (z) => Math.max(1.5, z.severity / 30),
      // Keep polygons visible behind icon clusters but in front of the
      // satellite raster — deck.gl's interleaved mode (set in WorldMap)
      // composes them at the right z.
      extensions: [],
      onClick: onWarZoneClick
        ? (info) => {
            const z = info.object as WarZone | null;
            if (z) {
              const payload: WarZoneInfo = {
                id: z.id,
                name: z.name,
                severity: z.severity,
                tag: z.tag,
                asOf: z.asOf,
              };
              onWarZoneClick(payload);
            }
            return true;
          }
        : null,
    });
  },
};
